import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ListaReordenable } from '@/components/ui/ListaReordenable';
import AppLayout from '@/components/Layouts/AppLayout';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { useTareasBase } from '../hooks/useTareasBase';

/**
 * Autogestión de la base genérica de tareas del checklist de escrituras.
 *
 * Los cambios acá solo impactan a las escrituras creadas (o sincronizadas)
 * a partir del cambio: las escrituras existentes conservan su copia.
 */
export const TareasBasePage: React.FC = () => {
  const { canManageTareasBase } = useAuthorization();
  const {
    tareasBase,
    isLoading,
    crearTareaBase,
    actualizarTareaBase,
    eliminarTareaBase,
    reordenarTareasBase,
    isGuardando,
  } = useTareasBase();

  const [nueva, setNueva] = useState('');

  const handleCrear = async () => {
    const nombre = nueva.trim();
    if (!nombre) {
      toast.error('El nombre de la tarea es obligatorio');
      return;
    }

    try {
      await crearTareaBase({ nombre });
      setNueva('');
    } catch {
      toast.error('No se pudo crear la tarea predeterminada.');
    }
  };

  const reordenar = async (ids: string[]) => {
    try {
      await reordenarTareasBase(ids);
    } catch {
      toast.error('No se pudo reordenar la base de tareas.');
    }
  };

  if (!canManageTareasBase) {
    return (
      <AppLayout prevButtonVisible={false}>
        <div className="py-16 text-center text-muted-foreground">
          No tenés permisos para administrar las tareas predeterminadas.
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout prevButtonVisible={false}>
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/" aria-label="Volver a escrituras">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Tareas predeterminadas</h1>
            <p className="text-sm text-muted-foreground">
              Solo las tareas activas se copian al crear una escritura. Los cambios no
              modifican el checklist de escrituras ya creadas.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Base de tareas</CardTitle>
          </CardHeader>

          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={nueva}
                onChange={(event) => setNueva(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void handleCrear();
                }}
                placeholder="Nombre de la tarea predeterminada"
                aria-label="Nueva tarea predeterminada"
                maxLength={255}
                disabled={isGuardando}
              />
              <Button onClick={() => void handleCrear()} disabled={isGuardando}>
                <Plus className="mr-1 h-4 w-4" />
                Agregar
              </Button>
            </div>

            {isLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p>
            ) : tareasBase.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Sin tareas predeterminadas todavía
              </p>
            ) : (
              <ListaReordenable
                as="ul"
                items={tareasBase}
                onReordenar={(ids) => void reordenar(ids)}
                etiquetaItem={(item) => item.nombre}
                className="space-y-2"
              >
                {(item, asa) => (
                  <div className="flex items-center gap-2 rounded-lg border bg-card p-2">
                    {asa}
                    <Input
                      defaultValue={item.nombre}
                      aria-label={`Editar nombre de ${item.nombre}`}
                      className="h-8 flex-1"
                      maxLength={255}
                      onBlur={(event) => {
                        const nombre = event.target.value.trim();
                        if (nombre && nombre !== item.nombre) {
                          void actualizarTareaBase({ id: item.id, data: { nombre } });
                        }
                      }}
                    />

                    <Button
                      size="sm"
                      variant="ghost"
                      title={item.activo ? 'Desactivar' : 'Activar'}
                      onClick={() =>
                        void actualizarTareaBase({ id: item.id, data: { activo: !item.activo } })
                      }
                    >
                      <Badge variant={item.activo ? 'default' : 'secondary'}>
                        {item.activo ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </Button>

                    <Button
                      size="icon"
                      variant="ghost"
                      title="Eliminar tarea"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => void eliminarTareaBase(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </ListaReordenable>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default TareasBasePage;
